import { getSourceBranch, isBranchSource } from "./options";
import type { OnboardingPatch, OnboardingState } from "./types";

export type StepId = "role" | "source";

/**
 * Two questions, then the editor. "source" is last, so it carries the Get
 * Started button (isFinalStep reads the end of this list).
 *
 * Two steps were removed, both for the same reason: they cost a click and
 * returned nothing.
 *
 * "intent" ("What should we make today?") was collected, stored on the user
 * record, and never read by the editor. The field stays on the auth contract so
 * existing answers survive and re-adding the step is a UI change rather than a
 * migration. If it returns it should DO something, seed the first project or
 * prime the Director, which is the version worth asking for.
 *
 * "welcome" was a greeting with no question. Once intent went, the gate closed
 * the wizard as soon as role and source were saved (it unmounts on
 * onboarding.completed, which is derived from those two fields), so welcome
 * became unreachable. It was also redundant: the editor's own first screen
 * already greets the user by name.
 */
export const STEP_ORDER: readonly StepId[] = ["role", "source"];

export interface WizardState {
  step: StepId;
  role: string | null;
  source: string | null;
  sourceDetail: string | null;
  /**
   * The source chip whose sub-question is currently on screen, or null when the
   * parent chip row is showing. Kept separate from `source` so that going back
   * out of a sub-question can return to the parent row rather than the previous
   * step.
   */
  branch: string | null;
}

export type WizardAction =
  /** A chip was picked on whichever row is currently visible. */
  | { type: "select"; value: string }
  /** The free-text source chip changed. */
  | { type: "setSourceDetail"; value: string }
  | { type: "continue" }
  | { type: "back" };

export function createInitialState(
  saved?: Partial<OnboardingState> | null,
): WizardState {
  return {
    step: "role",
    role: saved?.role ?? null,
    source: saved?.source ?? null,
    sourceDetail: saved?.sourceDetail ?? null,
    branch: null,
  };
}

function stepIndex(step: StepId): number {
  return STEP_ORDER.indexOf(step);
}

function nextStep(step: StepId): StepId {
  const next = STEP_ORDER[stepIndex(step) + 1];
  return next ?? step;
}

function previousStep(step: StepId): StepId {
  const previous = STEP_ORDER[stepIndex(step) - 1];
  return previous ?? step;
}

/**
 * Moves to `target`, deriving the sub-question state for that row. Arriving at
 * the source step with a branchy answer already stored reopens its
 * sub-question so the stored detail stays visible and editable.
 */
function enterStep(state: WizardState, target: StepId): WizardState {
  return {
    ...state,
    step: target,
    branch:
      target === "source" && isBranchSource(state.source)
        ? state.source
        : null,
  };
}

/** Whether the Continue button is enabled for the row currently on screen. */
export function canContinue(state: WizardState): boolean {
  switch (state.step) {
    case "role":
      return state.role !== null;
    case "source":
      if (state.branch !== null) {
        // A sub-question is open, so the parent chip alone is not an answer.
        return (state.sourceDetail ?? "").trim().length > 0;
      }
      return state.source !== null;
  }
}

/** Whether Back has anywhere to go. The first row is the start of the flow. */
export function canGoBack(state: WizardState): boolean {
  return state.step !== "role";
}

/**
 * The answer to persist when leaving `state.step`. Null when the step holds no
 * answer of its own (the welcome interstitial).
 */
export function patchForStep(state: WizardState): OnboardingPatch | null {
  switch (state.step) {
    case "role":
      return { role: state.role };
    case "source":
      return { source: state.source, sourceDetail: state.sourceDetail };
  }
}

/** True once Continue on this step finishes the whole flow. */
export function isFinalStep(step: StepId): boolean {
  return step === STEP_ORDER[STEP_ORDER.length - 1];
}

function selectOnSource(state: WizardState, value: string): WizardState {
  if (state.branch !== null) {
    const branch = getSourceBranch(state.branch);
    // The free-text branch is driven by setSourceDetail, not by chips.
    if (branch?.kind !== "chips") return state;
    return { ...state, sourceDetail: value };
  }

  // Picking a branchy parent swaps the row for its sub-question rather than
  // answering the step outright.
  if (isBranchSource(value)) {
    const reselectingSameBranch = state.source === value;
    return {
      ...state,
      source: value,
      branch: value,
      sourceDetail: reselectingSameBranch ? state.sourceDetail : null,
    };
  }

  return { ...state, source: value, sourceDetail: null, branch: null };
}

function goBack(state: WizardState): WizardState {
  // Inside a sub-question, Back returns to the parent chip row and clears the
  // half-made answer so Continue cannot advance on a bare parent chip.
  if (state.step === "source" && state.branch !== null) {
    return { ...state, branch: null, source: null, sourceDetail: null };
  }

  if (!canGoBack(state)) return state;

  return enterStep(state, previousStep(state.step));
}

export function wizardReducer(
  state: WizardState,
  action: WizardAction,
): WizardState {
  switch (action.type) {
    case "select":
      switch (state.step) {
        case "role":
          return { ...state, role: action.value };
        case "source":
          return selectOnSource(state, action.value);
      }
      break;

    case "setSourceDetail": {
      const branch = getSourceBranch(state.branch);
      if (state.step !== "source" || branch?.kind !== "text") return state;
      return { ...state, sourceDetail: action.value };
    }

    case "continue": {
      if (!canContinue(state) || isFinalStep(state.step)) return state;
      return enterStep(state, nextStep(state.step));
    }

    case "back":
      return goBack(state);
  }

  return state;
}
